import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { PageHeader } from '../components/common';
import { adminApi } from '../utils/api';

const Tags = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', category_id: '' });
  const [categoryFormData, setCategoryFormData] = useState({ name: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tagsRes, categoriesRes] = await Promise.all([
        adminApi.getTags(),
        adminApi.getTagCategories(),
      ]);
      setTags(tagsRes.data.tags || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      // Show empty state on error - no mock data
      setCategories([]);
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTagDialog = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setFormData({
        name: tag.name,
        description: tag.description || '',
        category_id: tag.category_id || '',
      });
    } else {
      setEditingTag(null);
      setFormData({ name: '', description: '', category_id: '' });
    }
    setDialogOpen(true);
  };

  const handleOpenCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({ name: category.name });
    } else {
      setEditingCategory(null);
      setCategoryFormData({ name: '' });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveTag = async () => {
    try {
      if (editingTag) {
        await adminApi.updateTag(editingTag.tag_id, formData);
      } else {
        await adminApi.createTag(formData);
      }
      fetchData();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save tag:', err);
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await adminApi.updateTagCategory(editingCategory.category_id, categoryFormData);
      } else {
        await adminApi.createTagCategory(categoryFormData);
      }
      fetchData();
      setCategoryDialogOpen(false);
    } catch (err) {
      console.error('Failed to save category:', err);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (window.confirm('Are you sure you want to delete this tag?')) {
      try {
        await adminApi.deleteTag(tagId);
        fetchData();
      } catch (err) {
        console.error('Failed to delete tag:', err);
      }
    }
  };

  const getTagsByCategory = (categoryId) => {
    return tags.filter((tag) => tag.category_id === categoryId);
  };

  const uncategorizedTags = tags.filter((tag) => !tag.category_id);

  return (
    <Box>
      <PageHeader
        title="tags"
        subtitle={`${tags.length} tags in ${categories.length} categories`}
        breadcrumbs={[{ label: 'tags' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryDialog()}
            >
              Add Category
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenTagDialog()}
            >
              Add Tag
            </Button>
          </Box>
        }
      />

      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {categories.map((category) => (
            <Grid item xs={12} md={6} key={category.category_id}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4">
                      {category.name}
                    </Typography>
                    <Box>
                      <Tooltip title="Edit category">
                        <IconButton size="small" onClick={() => handleOpenCategoryDialog(category)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {getTagsByCategory(category.category_id).map((tag) => (
                      <Chip
                        key={tag.tag_id}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {tag.name}
                            <Box
                              component="span"
                              sx={{
                                ml: 0.5,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                fontSize: '0.65rem',
                              }}
                            >
                              {tag.recipes_count || 0}
                            </Box>
                          </Box>
                        }
                        onDelete={() => handleDeleteTag(tag.tag_id)}
                        onClick={() => handleOpenTagDialog(tag)}
                        sx={{
                          backgroundColor: `${theme.palette.primary.main}15`,
                          '&:hover': {
                            backgroundColor: `${theme.palette.primary.main}25`,
                          },
                        }}
                      />
                    ))}
                    {getTagsByCategory(category.category_id).length === 0 && (
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        No tags in this category
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {uncategorizedTags.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    Uncategorized
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {uncategorizedTags.map((tag) => (
                      <Chip
                        key={tag.tag_id}
                        label={tag.name}
                        onDelete={() => handleDeleteTag(tag.tag_id)}
                        onClick={() => handleOpenTagDialog(tag)}
                        sx={{ backgroundColor: theme.palette.background.elevated }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Tag Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category_id}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
              label="Category"
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.category_id} value={cat.category_id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTag}>
            {editingTag ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={categoryFormData.name}
            onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCategory}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tags;
